import { Injectable } from '@angular/core';
import { RESULT_STATUSES, ResultStatus } from '../models/test-run.model';
import { RESULT_STATUS_ICON } from '../shared/test-run-status-icon';

/** Schluessel der projektweiten Ist-Status-Liste (`page-testlauf.jsx:79-82`). */
export const RUN_STATUS_KEY = 'om-run-status';

/**
 * Ein waehlbares Ist-Ergebnis im Runner.
 *
 * <p>`label` traegt nur eine eigene Beschriftung aus der Projektkonfiguration;
 * ohne sie bleibt die uebersetzte Beschriftung stehen, damit die Sprachwahl
 * weiter greift.
 */
export interface IstStatus {
  status: ResultStatus;
  icon: string;
  label: string | null;
}

/** Die vier Ist-Ergebnisse aus `page-testlauf.jsx:218-223`. */
export const RUN_STATUS_DEFAULT: readonly IstStatus[] = [
  { status: 'PASSED', icon: RESULT_STATUS_ICON.PASSED, label: null },
  { status: 'FAILED', icon: RESULT_STATUS_ICON.FAILED, label: null },
  { status: 'BLOCKED', icon: RESULT_STATUS_ICON.BLOCKED, label: null },
  { status: 'SKIPPED', icon: RESULT_STATUS_ICON.SKIPPED, label: null },
];

function readString(entry: Record<string, unknown>, field: string): string | null {
  const value = entry[field];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * Ein konfigurierter Eintrag wird nur angeboten, wenn der Server sein Ergebnis
 * kennt. Das Design-Bundle fuehrt daneben eigene Werte (`retest`, `klaerung`);
 * sie haben im Backend keine Entsprechung und wuerden bei jedem Klick in einem
 * 400 enden — angeboten wird deshalb nur, was auch gespeichert werden kann.
 */
function toStatus(entry: unknown): IstStatus | null {
  if (entry === null || typeof entry !== 'object') return null;
  const record = entry as Record<string, unknown>;
  const id = readString(record, 'id')?.toUpperCase();
  const status = RESULT_STATUSES.find((known) => known === id);
  if (!status) return null;
  return { status, icon: RESULT_STATUS_ICON[status], label: readString(record, 'label') };
}

/**
 * Die waehlbaren Ist-Ergebnisse des Runners (NEX-1042). Projekte hinterlegen
 * ihre Auswahl unter {@link RUN_STATUS_KEY}; ohne oder ohne brauchbaren Eintrag
 * gilt {@link RUN_STATUS_DEFAULT}.
 */
@Injectable({ providedIn: 'root' })
export class IstStatusService {
  statuses(): readonly IstStatus[] {
    const seen = new Set<ResultStatus>();
    const configured: IstStatus[] = [];
    for (const entry of this.read()) {
      const status = toStatus(entry);
      if (!status || seen.has(status.status)) continue;
      seen.add(status.status);
      configured.push(status);
    }
    return configured.length > 0 ? configured : RUN_STATUS_DEFAULT;
  }

  /**
   * Der ganze Zugriff liegt hinter einem `try`, weil `localStorage` in privaten
   * Sitzungen und bei gesperrten Cookies wirft — der Runner soll daran nicht
   * scheitern, sondern auf seine Vorgabe zurueckfallen.
   */
  private read(): unknown[] {
    try {
      const raw = localStorage.getItem(RUN_STATUS_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
